import { useState, useEffect } from 'react';
import { db, collection, query, orderBy, getDocs } from '../firebase';

// Module-level cache shared across all consumers (PromoPopup + PromoBanner) and
// across mounts within a session. Promotions change rarely, so a one-time cached
// fetch replaces a per-visitor, per-page persistent onSnapshot listener.
let cachedPromos = null;
let cacheTime = 0;
const PROMOS_TTL = 5 * 60 * 1000; // 5 minutes

// Shared list of active promotion banners — used by both the site-wide PromoPopup
// and the homepage PromoBanner so they stay in sync from one source.
// Single-field orderBy + client-side isActive filter avoids a composite index.
export function usePromotions() {
    const [promos, setPromos] = useState(cachedPromos || []);
    useEffect(() => {
        if (!db) return;
        // Serve fresh cache immediately without a read.
        if (cachedPromos && (Date.now() - cacheTime) < PROMOS_TTL) {
            setPromos(cachedPromos);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const q = query(collection(db, 'public/data/promotions'), orderBy('order', 'asc'));
                const s = await getDocs(q);
                const list = s.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.isActive && p.imageUrl);
                cachedPromos = list;
                cacheTime = Date.now();
                if (!cancelled) setPromos(list);
            } catch (e) { console.error('promotions:', e); }
        })();
        return () => { cancelled = true; };
    }, []);
    return promos;
}

// Follow a promo's link: external (http/https) opens a new tab; anything else is
// treated as an internal route and navigated via the router.
export function followPromoLink(linkUrl, navigate) {
    const link = (linkUrl || '').trim();
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
        window.open(link, '_blank', 'noopener,noreferrer');
    } else {
        navigate(link.startsWith('/') ? link : '/' + link);
    }
}
